import { IProvider, ProviderState } from "@microsoft/mgt-element";
import ls from "localstorage-slim";
import { isAuthenticated } from "../components/Common/Store/CommonSignals";

export class CookieProxyProvider extends IProvider {
  constructor(baseUrl, getHeadersFn) {
    super();
    this.baseUrl = baseUrl;
    this.getHeadersFn = getHeadersFn;
    this._isReady = false;

    // Create graph client manually
    this._graphClient = {
      api: (path) => {
        // Ensure path starts with /
        const normalizedPath = path.startsWith("/") ? path : `/${path}`;
        return new GraphRequest(this, normalizedPath);
      },
      forComponent: () => this._graphClient,
    };
  }

  async initialize() {
    this.setState(ProviderState.Loading);
    await new Promise((resolve) => setTimeout(resolve, 100));
    this.setState(ProviderState.SignedIn);
    this._isReady = true;
  }

  async executeRequest(method, path, queryParams, body = null) {
    if (!this._isReady) {
      throw new Error("Provider not initialized");
    }

    // Check cached suggested people
    if (path === "/me/people" && ls.get("suggestedPeople")) {
      return ls.get("suggestedPeople");
    }

    console.log(`Executing ${method} request to ${path}`);

    // Build URL with query parameters
    let url = `${this.baseUrl}${path}`;
    if (queryParams.size > 0) {
      const params = new URLSearchParams();
      queryParams.forEach((value, key) => {
        params.append(key, value);
      });
      url += `?${params.toString()}`;
    }

    const headers = await this.getHeadersFn();

    const options = {
      method,
      credentials: "include",
      headers: {
        ...headers,
      },
    };

    // Only set Content-Type for non-GET requests with body
    if (body) {
      options.headers["Content-Type"] = "application/json";
      options.body = JSON.stringify(body);
    }

    console.log(`CookieProxyProvider: ${method} ${url}`); // Debug logging

    const response = await fetch(url, options);
    if (response.status === 401) {
      isAuthenticated.value = false;
    }

    if (!response.ok) {
      const error = new Error(`Request failed: ${response.status}`);
      error.statusCode = response.status;
      throw error;
    }

    // Special handling for photo requests
    if (path.includes("/photo")) {
      console.log("Handling photo request:", path);
      const contentType = response.headers.get("content-type");

      if (contentType?.includes("image/")) {
        // Return the response directly for image data
        console.log("Returning image response");
        return response;
      } else {
        // If backend returns JSON with photo data/URL, handle it
        const data = await response.json();
        console.log("Photo response data:", data);
        return data;
      }
    }

    // Check content type for other responses
    const contentType = response.headers.get("content-type");
    if (contentType?.includes("image/")) {
      // Return response for image data
      return response;
    }

    // Default: parse as JSON
    const data = await response.json();

    // Cache /me/people response with 24h ttl
    if (path === "/me/people" && data) {
      ls.set("suggestedPeople", data, { ttl: 60 * 60 * 24 });
    }

    return data;
  }

  get graph() {
    return this._graphClient;
  }

  async getAccessToken() {
    if (this.state !== ProviderState.SignedIn) {
      throw new Error("Not authenticated");
    }
    return "cookie-auth";
  }

  async login() {
    this.setState(ProviderState.SignedIn);
    return Promise.resolve();
  }

  async logout() {
    this.setState(ProviderState.SignedOut);
    return Promise.resolve();
  }
}

// Helper class to handle Graph API request building
class GraphRequest {
  constructor(provider, path) {
    this.provider = provider;
    this.path = path;
    this.queryParams = new Map();
    this.headers = new Map();
  }

  select(fields) {
    this.queryParams.set("$select", fields);
    return this;
  }

  expand(fields) {
    this.queryParams.set("$expand", fields);
    return this;
  }

  top(count) {
    this.queryParams.set("$top", count);
    return this;
  }

  orderby(fields) {
    this.queryParams.set("$orderby", fields);
    return this;
  }

  filter(expression) {
    this.queryParams.set("$filter", expression);
    return this;
  }

  count(value = true) {
    this.queryParams.set("$count", value.toString());
    return this;
  }

  skip(count) {
    this.queryParams.set("$skip", count);
    return this;
  }

  search(query) {
    this.queryParams.set("$search", query);
    return this;
  }

  header(key, value) {
    this.headers.set(key, value);
    return this;
  }

  headers(headersObj) {
    Object.entries(headersObj).forEach(([key, value]) => {
      this.headers.set(key, value);
    });
    return this;
  }

  query(params) {
    Object.entries(params).forEach(([key, value]) => {
      this.queryParams.set(key, value);
    });
    return this;
  }

  middlewareOptions() {
    return this;
  }

  async get() {
    console.log(`GraphRequest.get() called for path: ${this.path}`);
    console.log(`Query params:`, Array.from(this.queryParams.entries()));

    // Special logging for photo requests
    if (this.path.includes("/photo")) {
      console.log("🖼️ Photo request detected:", this.path);
    }

    const result = await this.provider.executeRequest("GET", this.path, this.queryParams);

    // Log user data to see what fields are present
    if (this.path.includes("/users") && result) {
      console.log("User data received:", result);
    }

    // Log photo results
    if (this.path.includes("/photo")) {
      console.log("🖼️ Photo request result:", result);
    }

    return result;
  }
  async post(body) {
    return this.provider.executeRequest("POST", this.path, this.queryParams, body);
  }
  async patch(body) {
    return this.provider.executeRequest("PATCH", this.path, this.queryParams, body);
  }
  async put(body) {
    return this.provider.executeRequest("PUT", this.path, this.queryParams, body);
  }
  async delete() {
    return this.provider.executeRequest("DELETE", this.path, this.queryParams);
  }
  async getStream() {
    return this.get();
  }
  async putStream(stream) {
    return this.put(stream);
  }
}
